import React from 'react';
import { highlightedProductAdderSelector, clearHighlightedProductAdder } from '@src/features/product';
import { cartStateSelector, closeCartModal } from '@src/features/cart';
import IdProvider from '@src/core/IdProvider';
import { WidgetParamContext, useAppSelector, useAppDispatch } from '@src/core/hooks';
import CartDetails from '@src/components/Cart/CartDetails/CartDetails';
import { BackdropModal } from '@src/components/reusable';
import {
  termsSelector,
  currentTermsSelector,
  createdAccountSelector,
  resendEmailSelector,
  modifiedPasswordSelector,
  setCreatedAccount,
  setmodifiedPassword,
  buyerNeedsPhoneVerificationSelector,
} from '../../features/auth';
import TermsModal from '../Auth/Modals/TermsModal';
import SuccessModal from '../Auth/Modals/SuccessModal';
import ResendEmailModal from '../Auth/Modals/ResendEmailModal';
import useTranslate from '../../i18n/useTranslate';
import { DisapprovedPurchaseModal } from '../Checkout/Modals/DisapprovedPurchaseModal';
import { paymentErrorsSelector } from '@src/features/payment';
import { GatewayPaymentErrorModal } from '../Checkout/Modals/GatewayPaymentErrorModal';
import { isRequestTimeoutSelector, setRequestTimeout } from '@src/features/ui';
import { TimeoutModal } from '../Checkout/Modals/TimeoutModal';
import ResponsiveModal from '../ui2/ResponsiveModal';
import AdderRootContainer from '../reusable/AdderRootContainer';
import AdderContainer from './AdderContainer';

const BodySetup = () => {
  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();
  const terms = useAppSelector(termsSelector);
  const errors = useAppSelector(paymentErrorsSelector);
  const resendEmail = useAppSelector(resendEmailSelector);
  const { isModalOpened } = useAppSelector(cartStateSelector);
  const currentTermState = useAppSelector(currentTermsSelector);
  const accountCreated = useAppSelector(createdAccountSelector);
  const isRequestTimeout = useAppSelector(isRequestTimeoutSelector);
  const modifiedPassword = useAppSelector(modifiedPasswordSelector);
  const highlightedPA = useAppSelector(highlightedProductAdderSelector);

  const needsPhoneVerification = useAppSelector(buyerNeedsPhoneVerificationSelector);

  const showModalTerms = terms != null && currentTermState != null;
  const isPurchaseDisapproved = Boolean(errors['transactionError']);
  const isPurchaseGatewaysMismatch = errors['paymentMethodId']?.includes('gatewaysMustMatch') ?? false;
  const isPurchaseGatewaysBlockedBuyer = errors['clientInfo']?.includes('blockedBuyer') ?? false;

  const closeRef = React.useRef<() => void>(null);

  const showAccountCreatedSuccessModal = accountCreated && !needsPhoneVerification;

  const isVisible =
    isModalOpened ||
    highlightedPA != null ||
    showModalTerms ||
    showAccountCreatedSuccessModal ||
    resendEmail ||
    isPurchaseDisapproved ||
    modifiedPassword ||
    isPurchaseGatewaysMismatch ||
    isPurchaseGatewaysBlockedBuyer;

  const handleClose = () => {
    if (isModalOpened) {
      dispatch(closeCartModal());
    }

    if (closeRef.current) {
      closeRef.current();
    } else if (highlightedPA) {
      dispatch(clearHighlightedProductAdder());
    }
  };

  return (
    <>
      <ResponsiveModal
        open={isRequestTimeout}
        onClose={() => dispatch(setRequestTimeout(false))}
        showCloseButton={false}
        width={552}
      >
        <TimeoutModal />
      </ResponsiveModal>

      <BackdropModal visible={isVisible} closeModal={handleClose}>
        {!isPurchaseDisapproved &&
          !showModalTerms &&
          !showAccountCreatedSuccessModal &&
          !resendEmail &&
          !modifiedPassword &&
          !highlightedPA && <CartDetails />}

        {highlightedPA && (
          <IdProvider id={highlightedPA.id}>
            <WidgetParamContext.Provider value={highlightedPA.params}>
              <AdderContainer>
                <AdderRootContainer ref={closeRef} />
              </AdderContainer>
            </WidgetParamContext.Provider>
          </IdProvider>
        )}

        {showModalTerms && terms[currentTermState] ? (
          <TermsModal document={terms[currentTermState]!} currentTerm={currentTermState} />
        ) : null}

        {showAccountCreatedSuccessModal ? (
          <SuccessModal
            textInfo={i18n('auth_messageAccountCreatedSuccessFully')}
            textButton={i18n('checkout_continueShopping')}
            handleClose={() => dispatch(setCreatedAccount(false))}
          />
        ) : null}

        {resendEmail && <ResendEmailModal />}

        {modifiedPassword && (
          <SuccessModal
            textInfo={i18n('auth_messagePasswordModifiedSuccessFully')}
            textButton={i18n('auth_login')}
            handleClose={() => dispatch(setmodifiedPassword(false))}
          />
        )}

        {isPurchaseDisapproved && <DisapprovedPurchaseModal descriptions={errors['transactionError']} />}

        {isPurchaseGatewaysMismatch && <GatewayPaymentErrorModal />}

        {isPurchaseGatewaysBlockedBuyer && (
          <GatewayPaymentErrorModal
            title={i18n('checkout_payment_gateway_blocked_buyer_error_title')}
            helperText={i18n('checkout_payment_gateway_blocked_buyer_error_helper_text')}
          />
        )}
      </BackdropModal>
    </>
  );
};

export default BodySetup;
